import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Trophy } from "lucide-react";
import axios from "../../../../axios";
import { toast } from "react-toastify";
import { useUser } from "../../../../hooks/use-user";

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getRowsFromResponse = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;
  const candidates = [
    result?.leaderboard,
    result?.users,
    result?.topUsers,
    result?.results,
    result?.attempts,
    result,
    data?.leaderboard,
    data?.users,
    data?.topUsers,
    data,
  ];

  return candidates.find((item) => Array.isArray(item)) || [];
};

const getSummaryFromResponse = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  if (result && !Array.isArray(result) && typeof result === "object") return result;
  if (data && !Array.isArray(data) && typeof data === "object") return data;

  return {};
};

const getUserName = (row) => {
  const user = row?.user || row?.userId || row?.student || {};

  return pickFirst(
    row?.name,
    row?.userName,
    row?.studentName,
    user?.name,
    user?.fullName,
    "User",
  );
};

const getUserId = (row) => {
  const user = row?.user || row?.userId || row?.student || {};

  return String(pickFirst(row?.userId, row?.studentId, user?._id, row?._id, ""));
};

const getScore = (row) =>
  pickFirst(row?.score, row?.marks, row?.totalScore, row?.finalScore, 0);

const getTime = (row) => {
  const explicitTime = pickFirst(
    row?.timeTakenSeconds,
    row?.totalTimeTaken,
    row?.totalTime,
    row?.attempt?.timeTakenSeconds,
    row?.attempt?.timeTaken,
    row?.timeTaken,
  );

  if (explicitTime !== undefined) return explicitTime;

  const startedAt = row?.startedAt ? new Date(row.startedAt).getTime() : null;
  const submittedAt = row?.submittedAt ? new Date(row.submittedAt).getTime() : null;

  if (startedAt && submittedAt && submittedAt > startedAt) {
    return Math.round((submittedAt - startedAt) / 1000);
  }

  return 0;
};

const MockTestLeaderboard = () => {
  const { mockTestId } = useParams();
  const { data: user } = useUser();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);

      try {
        const response = await axios.get(`/mock-tests/${mockTestId}/leaderboard`);
        setRows(getRowsFromResponse(response));
        setSummary(getSummaryFromResponse(response));
      } catch (error) {
        toast.error("Unable to load leaderboard");
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [mockTestId]);

  const normalizedRows = useMemo(
    () =>
      rows.map((row, index) => ({
        id: getUserId(row) || String(index),
        rank: pickFirst(row?.rank, index + 1),
        name: getUserName(row),
        score: getScore(row),
        time: getTime(row),
        raw: row,
      })),
    [rows],
  );

  const currentUserId = String(user?._id || "");
  const currentUserRank = pickFirst(
    summary?.currentUserRank,
    summary?.userRank,
    normalizedRows.find((row) => row.id && row.id === currentUserId)?.rank,
    "-",
  );
  const submittedAttempts = pickFirst(
    summary?.submittedAttempts,
    summary?.totalAttempts,
    summary?.attemptsCount,
    normalizedRows.length,
  );

  return (
    <div className="min-h-screen bg-[#fbfafc] pb-28 text-[#211629]">
      {/* <header className="sticky top-0 z-30 border-b border-[#eee8f0] bg-white/95 px-4 py-4 shadow-sm backdrop-blur lg:static lg:px-8">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#f7f5f8] text-[#211629] shadow-sm transition hover:scale-95"
              aria-label="Back"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="truncate text-2xl font-semibold text-[#211629]">
              Leaderboard
            </h1>
          </div>

          <button
            type="button"
            onClick={() => navigate("/dash/wallet")}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#f7f1f8] text-[#562566] shadow-sm"
            aria-label="Wallet"
          >
            <Wallet className="h-6 w-6" />
          </button>
        </div>
      </header> */}

      <main className="mx-auto lg:mt-10 max-w-5xl px-4 py-4 sm:px-6 lg:px-8">
        <section className="flex items-center gap-5 rounded-[1.8rem] bg-[#562566] px-4 py-6 text-left text-white shadow-[0_14px_30px_rgba(86,37,102,0.18)]">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white/14">
            <Trophy className="h-10 w-10 " />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold leading-tight">
              Mock Test Leaderboard
            </h2>
            <p className="mt-2 text-md font-semibold text-white/82">
              {submittedAttempts} submitted attempts
            </p>
          </div>
        </section>

        {loading ? (
          <div className="mt-6 flex min-h-[260px] items-center justify-center rounded-[1.5rem] bg-white shadow-sm">
            <Loader2 className="h-8 w-8 animate-spin text-[#562566]" />
          </div>
        ) : (
          <>
            <section className="mt-6 flex items-center justify-between rounded-[1.5rem] bg-[#f7f0f9] px-6 py-6 text-[#562566]">
              <h3 className="text-xl font-bold">Your Rank</h3>
              <span className="text-3xl font-bold text-[#211629]">
                {currentUserRank === "-" ? "-" : `#${currentUserRank}`}
              </span>
            </section>

            <section className="mt-6 space-y-4">
              {normalizedRows.map((row) => {
                const isCurrentUser = row.id && row.id === currentUserId;

                return (
                  <article
                    key={`${row.rank}-${row.id}-${row.name}`}
                    className={`flex items-center gap-4 rounded-[1.5rem] px-3 py-2 shadow-sm ${
                      isCurrentUser
                        ? "border border-[#d8b9ea] bg-[#f1e5f8]"
                        : "bg-[#f7f3f8]"
                    }`}
                  >
                    <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded-full bg-[#562566] text-xl font-bold text-white">
                      #{row.rank}
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <h3 className="truncate text-xl font-bold text-[#211629]">
                        {row.name}
                      </h3>
                      <p className="mt-2 text-lg font-medium text-[#6e6875]">
                        Time: {row.time}s
                      </p>
                    </div>
                    <span className="shrink-0 text-2xl font-bold text-[#562566]">
                      {row.score}
                    </span>
                  </article>
                );
              })}

              {!normalizedRows.length && (
                <div className="rounded-[1.5rem] bg-white px-5 py-10 text-center shadow-sm">
                  <h3 className="text-xl font-bold text-[#211629]">
                    No submitted attempts yet
                  </h3>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default MockTestLeaderboard;
